import React, { useState } from 'react';
import { Outlet, useLocation, matchPath } from 'react-router-dom';
import NavBar from './NavBar';
import SideBar from './SideBar';
import { useStoreId } from './ProductedRoute';
import { useTheme } from '@mui/material/styles';

const drawerWidth = 300;
const navBarHeight = 64;

// Base layout routes (static)
const layoutRoutes = [
  '/layout/manage-choice-group',
  '/layout/manageItemCategory',
  '/layout/manage-choice',
  '/layout/manage-meal',
  '/layout/item-management',
  '/layout/orderManagement',
  '/layout/orderManagement/view',
  '/layout/stockManagement',
  '/layout/storeManagement',
  '/layout/manage-item-review',
  '/layout/manage-restaurant-review',
  '/layout/manage-order-review',
  '/layout/no-store',
  '/layout/dashboard',
  '/layout/cancellation-payment',
  '/layout/commissionTracking',
  '/layout/commissionTrackingHistory',
  '/layout/makeyourRestaurant',
  '/layout/settlements',
  '/layout/reports',
  '/layout/reports/sales',
  '/layout/reports/order-summary',
  '/layout/reports/admin-commission',
  '/layout/profile',
  '/layout/settings',
  '/layout/change-password'
];

const ProtectedLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const location = useLocation();
  const storeId = useStoreId();
  const noStoreAssigned = !storeId; // when userDetails.storeIds is empty
  const theme = useTheme();
  const isRTL = theme.direction === 'rtl';

  // Check if the path is /layout/manageItemSubCategory/:categoryId
  const matchSubCategory = matchPath(
    { path: '/layout/manageItemSubCategory/:categoryId', end: true },
    location.pathname
  );

  // Check if the path is /layout/orderManagement/view/:id
  const matchOrderView = matchPath(
    { path: '/layout/orderManagement/view/:id', end: true },
    location.pathname
  );

  // Check if the path is a reports route
  const matchReports = matchPath(
    { path: '/layout/reports/*', end: false },
    location.pathname
  );

  // Extract categoryId if matched
  const categoryId = matchSubCategory?.params?.categoryId;
  const orderId = matchOrderView?.params?.id;

  // Check if current path is one of the known static paths
  const isStaticKnownRoute = layoutRoutes.includes(location.pathname);

  // isKnownRoute is either a static match or a match for dynamic routes with valid parameters
  const isKnownRoute = isStaticKnownRoute || Boolean(categoryId) || Boolean(orderId) || Boolean(matchReports);

  const isNoStorePage = location.pathname === '/layout/no-store';

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <NavBar onDrawerToggle={handleDrawerToggle} />
      <div style={{ display: 'flex', flexGrow: 1, overflowX: 'hidden' }}>
        {isKnownRoute && !isNoStorePage && !noStoreAssigned && (
          <SideBar open={drawerOpen} onClose={handleDrawerToggle} />
        )}
        <main
          style={{
            flexGrow: 1,
            padding: '24px',
            marginTop: `${navBarHeight}px`,
            // Shift content opposite to the sidebar side
            marginLeft: isRTL ? '0' : (isKnownRoute && !isNoStorePage && !noStoreAssigned && drawerOpen ? `${drawerWidth}px` : '0'),
            marginRight: isRTL ? (isKnownRoute && !isNoStorePage && !noStoreAssigned && drawerOpen ? `${drawerWidth}px` : '0') : '0',
            width: isKnownRoute && !isNoStorePage && !noStoreAssigned && drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
            transition: 'margin 0.3s ease, width 0.3s ease',
            overflowX: 'hidden',
            minHeight: `calc(100vh - ${navBarHeight}px)`,
            boxSizing: 'border-box',
          }}
        >
          {noStoreAssigned && !isNoStorePage ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '60vh',
              color: '#7a2b4a',
              fontWeight: 600,
              fontSize: '18px'
            }}>
              No store assigned. Please contact administrator.
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

export default ProtectedLayout;
